import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import { Divider } from "components/design/Divider";
import { DownloadIcon } from "components/icons/Download";
import AddCrmEntityTableModal from "components/salesCrm/AddCrmEntityTableModal";
import NewGroupCreationModal from "components/salesCrm/NewGroupEntityModal";
import { YYYY_MM_DD } from "constants/date";
import {
  NOT_COMPLETED,
  NOT_CREATED,
  NOT_STARTED_KEY,
} from "constants/taskStatus";
import dayjs from "dayjs";
import { CRM_SUBSCRIPTION_TYPE_OPTIONS } from "dictionaries";
import { Field, Form, Formik } from "formik";
import { useFilters } from "hooks/useFilter";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useContext, useEffect, useState } from "react";
import {
  OTHER_FILTER_MULTIPLE_US_ENTITIES,
  OTHER_FILTER_STARRED_CHAT,
  OTHER_FILTERS_QUERY_KEY,
  TASK_TEMPLATE_FILTER,
} from "types/contants/crm";
import { debounce } from "utils/debouncing";
import { formatDateByViewType } from "utils/formatDate";
import { ADDTAG } from "../../constants/filterSelect";
import authContext from "../../jwt_context&axios/authContext";
import CartAdd from "static/images/CartAdd.svg";
import { useModal } from "hooks/useModal";
import { BulkCartActionModal } from "./BulkCartAction/BulkCartActionModal";
import { CrmFieldFilterModal } from "./CrmFieldFilter/CrmFieldFilterModal";
import { CrmFieldExportModal } from "./CrmFieldExport/CrmFieldExportModal";
import { CRM_FILTERS_QUERY_KEY } from "constants/crmFieldFilter";
import { encodeCrmFilters } from "util/crmFilter/encodeCrmFilters";
import { decodeCrmFilters } from "util/crmFilter/decodeCrmFilters";
import { Export } from "components/icons/Export";

const subscriptionOptions = [
  {
    value: "NO_SUBSCRIPTION",
    label: "No Subscription",
  },
  {
    value: "ACTIVE_SUBSCRIPTION",
    label: "Active Subscriber",
  },
  {
    value: "INACTIVE_SUBSCRIPTION",
    label: "Inactive Subscriber",
  },
];

const taskTemplateFilterMap = {
  [NOT_CREATED]: "Not Created",
  [NOT_STARTED_KEY]: "Not Started",
  [NOT_COMPLETED]: "Not Completed",
};

const OTHER_FILTER_OPTIONS = [
  {
    label: "Starred Chat",
    value: OTHER_FILTER_STARRED_CHAT,
  },
  {
    label: "Multiple US Entities",
    value: OTHER_FILTER_MULTIPLE_US_ENTITIES,
  },
];

export default function ServiceTeamFilter({
  searchedTerm,
  tags,
  selectedSubscription,
  subscriptions,
  templates,
  setShowTagModal,
  selectedTag,
  totalPageCount,
  totalItemCount,
  selectedAgent,
  assignMembers,
  usStates,
  usIncorporationMonths,
  selectedSubscriberType,
  sortDueBy,
  selectedTemplate,
  selectedLaunchedTemplate,
  update,
  paginationData,
  downloadCSV,
  isFetching,
  generateReport,
  selectedSalesperson,
  selectedStates,
  selectedIncorporationMonths,
  opsCrmFilters,
  otherFilters,
  selectedRows,
  resetSelection,
}) {
  const { authtoken } = useContext(authContext);
  const { isAdmin } = useRoleBasedView();
  const query = useQuery();
  const taskTemplateFilter = query.get(TASK_TEMPLATE_FILTER) || null;
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [userDataPayload, setUserDataPayLoad] = useState();
  const [groupName, setGroupName] = useState("");
  const { isForeignCA, isUserTypeForeignCA } = useRoleBasedView();
  const showAddModal = query.get("addclient");
  const { update: updateQuery } = useUpdateQuery();
  const bulkCartAction = useModal();
  const crmFieldFilterModal = useModal();
  const crmFieldExportModal = useModal();
  const [appliedExportFieldKeys, setAppliedExportFieldKeys] = useState([]);
  const otherFilterList = otherFilters?.split(",") ?? [];
  const appliedCrmFilters = decodeCrmFilters(query.get(CRM_FILTERS_QUERY_KEY));
  // Whatever the group list currently has applied, so the export matches what's on screen -
  // same source the group list query itself reads from (see useQuery).
  const exportFilterParams = Object.fromEntries(query.entries());

  const applyCrmFilters = (filters) => {
    update({ query: CRM_FILTERS_QUERY_KEY, value: encodeCrmFilters(filters) });
  };

  const clearCrmFilters = () => {
    update({ query: CRM_FILTERS_QUERY_KEY, value: null });
  };

  const { values: dateFilter, updateFilter } = useFilters({
    initialValue: {
      SINCE_DATE: "",
    },
    useQueryParams: true,
  });

  useEffect(() => {
    if (showAddModal) {
      setShowProfileForm(true);
      updateQuery({ query: "addclient", value: null });
    }
  }, [showAddModal]);

  const searchQuery = debounce((e) => {
    const { value } = e.target;
    if (value) {
      update({ query: "search_term", value: e.target.value });
    } else {
      update({ query: "search_term", value: null });
    }
  });

  const tagsFilter = (value) => {
    if (value === ADDTAG) {
      setShowTagModal(true);
      value = selectedTag;
    } else if (value) {
      update({ query: "tag_filter", value: value });
    } else {
      update({ query: "tag_filter", value: null });
    }
  };

  const templateFilter = (values) => {
    if (values) {
      update({ query: "not_launched_task_filter", value: values.join(",") });
    } else {
      update({ query: "not_launched_task_filter", value: null });
    }
  };

  const launchedTemplateFilter = (values) => {
    if (values) {
      update({ query: "launched_task_filter", value: values.join(",") });
    } else {
      update({ query: "launched_task_filter", value: null });
    }
  };

  const subscriptionFilter = (values) => {
    if (values) {
      update({ query: "subscription_type", value: values.join(",") });
    } else {
      update({ query: "subscription_type", value: null });
    }
  };

  const agentFilter = (values) => {
    if (values) {
      update({ query: "agent_filter", value: values.join(",") });
    } else {
      update({ query: "agent_filter", value: null });
    }
  };

  const salesPersonFilter = (values) => {
    if (values) {
      update({ query: "salesperson_filter", value: values.join(",") });
    } else {
      update({ query: "salesperson_filter", value: null });
    }
  };

  const usStateFilter = (values) => {
    if (values) {
      update({ query: "us_state_uuids", value: values.join(",") });
    } else {
      update({ query: "us_state_uuids", value: null });
    }
  };

  const usIncorporationMonthFilter = (values) => {
    if (values) {
      update({ query: "us_incorp_months", value: values.join(",") });
    } else {
      update({ query: "us_incorp_months", value: null });
    }
  };

  const subscriberFilter = (value) => {
    if (value) {
      update({ query: "subscriber_filter", value: value });
    } else {
      update({ query: "subscriber_filter", value: null });
    }
  };

  const duesFilter = (value) => {
    if (value) {
      update({ query: "sort_order", value: value });
    } else {
      update({ query: "sort_order", value: null });
    }
  };

  const taskFilter = (value) => {
    if (value) {
      update({ query: TASK_TEMPLATE_FILTER, value: value });
    } else {
      update({ query: TASK_TEMPLATE_FILTER, value: null });
    }
  };

  const updateTagFilter = (values) => {
    if (values) {
      update({ query: "tag_filter", value: values.join(",") });
    } else {
      update({ query: "tag_filter", value: null });
    }
  };

  const handleOtherFilterChange = ({ name, checked }) => {
    const current = otherFilters ? otherFilters.split(",") : [];

    const updated = checked
      ? [...current, name]
      : current.filter((v) => v !== name);

    update({
      query: OTHER_FILTERS_QUERY_KEY,
      value: updated.length ? updated.join(",") : null,
    });
  };

  const tagOptions = [
    { value: "NO_TAG", label: "No Tag" },
    ...(tags?.map((tag) => ({
      value: tag.title,
      label: `#${tag.title}`,
    })) || []),
  ];

  return (
    <>
      <div className="t-w-full t-pl-5 t-space-y-4 sm:t-box-border sm:t-px-4">
        <div className="t-flex t-justify-between t-items-center sm:t-flex-col sm:t-items-stretch sm:t-gap-3">
          <span className="t-flex t-items-center t-gap-3 t-w-1/2 sm:t-w-full">
            <Search
              placeholder="Search"
              block
              onChange={searchQuery}
              defaultValue={searchedTerm}
            />
          </span>

          <div className="t-mr-4 t-flex t-items-center t-gap-2 sm:t-mr-0 sm:t-flex-wrap">
            {!isForeignCA && (
              <>
                <Button
                  size="small"
                  leftIcon={<img src={CartAdd} alt="add to cart" />}
                  onClick={bulkCartAction.open}
                >
                  Bulk cart<span className="sm:t-hidden">&nbsp;action</span>
                </Button>
                <Button
                  onClick={downloadCSV}
                  customType="icon"
                  size="small"
                  isLoading={isFetching && generateReport}
                >
                  <span className="t-text-text-30">
                    <DownloadIcon color="currentColor" size={20} />
                  </span>
                </Button>
                <Button
                  size="small"
                  leftIcon={<Export />}
                  onClick={crmFieldExportModal.open}
                  active={appliedExportFieldKeys.length > 0}
                >
                  Export<span className="sm:t-hidden">&nbsp;CRM</span>
                  &nbsp;fields
                </Button>
              </>
            )}

            {isForeignCA && (
              <Button
                size="small"
                customType="primary"
                onClick={() => setShowProfileForm(true)}
              >
                + Add Client
              </Button>
            )}
          </div>
        </div>

        <div className="t-flex t-justify-between t-items-start t-mr-4 t-mb-4 sm:t-mr-0 sm:t-flex-col sm:t-gap-3">
          <div className="t-flex t-items-center t-flex-wrap t-gap-3">
            <Filter.Root
              defaultValue="TAG"
              title="Filter"
              capsule={
                <div className="t-flex t-gap-2 ">
                  {dateFilter.SINCE_DATE && (
                    <Chip
                      onRemove={() => updateFilter("SINCE_DATE", "")}
                      isActive
                      filterType="BANK_DISCONNECTED_SINCE"
                    >
                      {dateFilter.SINCE_DATE && (
                        <>
                          Since{" "}
                          {formatDateByViewType(
                            dateFilter.SINCE_DATE,
                            "TOTALS"
                          )}
                        </>
                      )}
                    </Chip>
                  )}

                  {otherFilterList?.length > 0 &&
                    OTHER_FILTER_OPTIONS.map(
                      (option) =>
                        otherFilterList.includes(option.value) && (
                          <Chip
                            key={option.value}
                            onRemove={() =>
                              handleOtherFilterChange({
                                name: option.value,
                                checked: false,
                              })
                            }
                            isActive
                            filterType="OTHER"
                          >
                            {option.label}
                          </Chip>
                        )
                    )}

                  {selectedAgent?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => agentFilter("")}
                      isActive
                      filterType="AGENT"
                    >
                      Agent ({selectedAgent.split(",").length})
                    </Chip>
                  )}

                  {selectedTemplate?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => templateFilter("")}
                      isActive
                      filterType="NOT_LAUNCHED_FILING"
                    >
                      Not Launched Filing ({selectedTemplate.split(",").length})
                    </Chip>
                  )}
                  {selectedLaunchedTemplate?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => launchedTemplateFilter("")}
                      isActive
                      filterType="LAUNCHED_FILING"
                    >
                      Launched Filing (
                      {selectedLaunchedTemplate.split(",").length})
                    </Chip>
                  )}

                  {selectedTag?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => updateTagFilter("")}
                      isActive
                      filterType="TAG"
                    >
                      Tag ({selectedTag.split(",").length})
                    </Chip>
                  )}
                  {selectedSubscription?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => subscriptionFilter("")}
                      isActive
                      filterType="SUBSCRIPTION"
                    >
                      Subscription ({selectedSubscription.split(",").length})
                    </Chip>
                  )}

                  {selectedSalesperson?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => salesPersonFilter("")}
                      isActive
                      filterType="SALESPERSON"
                    >
                      Sales Person ({selectedSalesperson.split(",").length})
                    </Chip>
                  )}

                  {selectedStates?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => usStateFilter("")}
                      isActive
                      filterType="US_STATE"
                    >
                      US state ({selectedStates.split(",").length})
                    </Chip>
                  )}

                  {selectedIncorporationMonths?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => usIncorporationMonthFilter("")}
                      isActive
                      filterType="INCORP_MONTH"
                    >
                      Incorp Month (
                      {selectedIncorporationMonths.split(",").length})
                    </Chip>
                  )}

                  {appliedCrmFilters.length > 0 && (
                    <Chip
                      onRemove={clearCrmFilters}
                      onClick={crmFieldFilterModal.open}
                      isActive
                      preventOpenOnClick
                    >
                      CRM Fields ({appliedCrmFilters.length})
                    </Chip>
                  )}
                </div>
              }
            >
              <Filter.Portal>
                <Filter.List>
                  {!isUserTypeForeignCA && !isForeignCA && (
                    <Filter.ListItem value="TAG">Tag</Filter.ListItem>
                  )}
                  {isAdmin && (
                    <Filter.ListItem value="AGENT">Agent</Filter.ListItem>
                  )}
                  {isAdmin && (
                    <Filter.ListItem value="SUBSCRIPTION">
                      Subscription
                    </Filter.ListItem>
                  )}
                  <Filter.ListItem value="NOT_LAUNCHED_FILING">
                    Not Launched Filing
                  </Filter.ListItem>
                  <Filter.ListItem value="LAUNCHED_FILING">
                    Launched Filing
                  </Filter.ListItem>
                  <Filter.ListItem value="SALESPERSON">
                    Sales Person
                  </Filter.ListItem>
                  <Filter.ListItem value="US_STATE">US State</Filter.ListItem>
                  <Filter.ListItem value="INCORP_MONTH">
                    Incorporation Month
                  </Filter.ListItem>
                  <Filter.ListItem value="BANK_DISCONNECTED_SINCE">
                    Bank disconnected since
                  </Filter.ListItem>
                  <Filter.ListItem value="OTHER">Other</Filter.ListItem>
                </Filter.List>

                <Filter.Body value="BANK_DISCONNECTED_SINCE" block>
                  <Formik
                    enableReinitialize
                    initialValues={{
                      SINCE_DATE: dateFilter.SINCE_DATE,
                    }}
                    onSubmit={() => {}}
                  >
                    <Form className="all:unset t-flex t-flex-col t-gap-4">
                      <Field name="SINCE_DATE">
                        {({ field }) => {
                          return (
                            <DateInput
                              {...field}
                              maxDate={new Date()}
                              label="Since"
                              onDateChange={(date) => {
                                updateFilter(
                                  "SINCE_DATE",
                                  dayjs(date).format(YYYY_MM_DD)
                                );
                              }}
                              portalId="callStart"
                            />
                          );
                        }}
                      </Field>
                    </Form>
                  </Formik>
                </Filter.Body>
                {isAdmin && (
                  <Filter.Body value="AGENT" block>
                    <MultiSelectFilter
                      onChange={agentFilter}
                      options={[
                        { value: "NOT_ASSIGNED", label: "Not Assigned" },
                        ...assignMembers?.map((assignee) => ({
                          value: assignee.profile_id,
                          label: assignee.name,
                        })),
                      ]}
                      selected={selectedAgent?.split(",")}
                    />
                  </Filter.Body>
                )}

                {isAdmin && (
                  <Filter.Body value="NOT_LAUNCHED_FILING" block>
                    <MultiSelectFilter
                      onChange={templateFilter}
                      options={templates?.map((template) => ({
                        value: template.uuid,
                        label: `${template.title}  ${
                          !template.year ? "" : `- ${template.year}`
                        }`,
                      }))}
                      selected={selectedTemplate?.split(",")}
                    />
                  </Filter.Body>
                )}

                {isAdmin && (
                  <Filter.Body value="LAUNCHED_FILING" block>
                    <MultiSelectFilter
                      onChange={launchedTemplateFilter}
                      options={[
                        {
                          label: "No Launched Filings",
                          value: "NO_LAUNCHED_TASKS",
                        },
                        ...templates?.map((template) => ({
                          value: template.uuid,
                          label: `${template.title}  ${
                            !template.year ? "" : `- ${template.year}`
                          }`,
                        })),
                      ]}
                      selected={selectedLaunchedTemplate?.split(",")}
                    />
                  </Filter.Body>
                )}

                {!isUserTypeForeignCA && !isForeignCA && (
                  <Filter.Body value="TAG" block>
                    {tags && (
                      <MultiSelectFilter
                        onChange={updateTagFilter}
                        options={tagOptions}
                        selected={selectedTag?.split(",")}
                      />
                    )}
                  </Filter.Body>
                )}
                {isAdmin && (
                  <Filter.Body value="SUBSCRIPTION" block>
                    <MultiSelectFilter
                      isSearchable={false}
                      onChange={subscriptionFilter}
                      options={CRM_SUBSCRIPTION_TYPE_OPTIONS}
                      selected={selectedSubscription?.split(",")}
                    />
                  </Filter.Body>
                )}
                <Filter.Body value="SALESPERSON" block>
                  <MultiSelectFilter
                    onChange={salesPersonFilter}
                    options={assignMembers?.map((assignee) => ({
                      value: assignee.profile_id,
                      label: assignee.name,
                    }))}
                    selected={selectedSalesperson?.split(",")}
                  />
                </Filter.Body>
                <Filter.Body value="US_STATE" block>
                  <MultiSelectFilter
                    onChange={usStateFilter}
                    options={usStates?.map((state) => ({
                      value: state.state_id,
                      label: state.name,
                    }))}
                    selected={selectedStates?.split(",")}
                  />
                </Filter.Body>
                <Filter.Body value="INCORP_MONTH" block>
                  <MultiSelectFilter
                    onChange={usIncorporationMonthFilter}
                    options={usIncorporationMonths?.map((month) => ({
                      value: month.month_id,
                      label: month.name,
                    }))}
                    selected={selectedIncorporationMonths?.split(",")}
                  />
                </Filter.Body>
                <Filter.Body value="OTHER" block>
                  {OTHER_FILTER_OPTIONS.map((option) => (
                    <Checkbox
                      key={option.value}
                      label={option.label}
                      name={option.value}
                      onChange={(e) => {
                        handleOtherFilterChange({
                          name: e.target.name,
                          checked: e.target.checked,
                        });
                      }}
                      checked={otherFilterList.includes(option.value)}
                    />
                  ))}
                </Filter.Body>
              </Filter.Portal>
            </Filter.Root>

            <Button
              type="button"
              size="small"
              onClick={crmFieldFilterModal.open}
              active={appliedCrmFilters.length > 0}
            >
              Advanced
            </Button>

            {crmFieldFilterModal.isOpen && (
              <CrmFieldFilterModal
                isOpen
                onClose={crmFieldFilterModal.close}
                appliedFilters={appliedCrmFilters}
                onApply={applyCrmFilters}
              />
            )}

            {crmFieldExportModal.isOpen && (
              <CrmFieldExportModal
                isOpen
                onClose={crmFieldExportModal.close}
                appliedFieldKeys={appliedExportFieldKeys}
                onExported={setAppliedExportFieldKeys}
                filterParams={exportFilterParams}
              />
            )}

            {!isUserTypeForeignCA && !isForeignCA && (
              <div className="t-flex t-gap-3 sm:t-grid sm:t-w-full sm:t-auto-cols-fr sm:t-grid-flow-col sm:t-gap-2">
                {isAdmin && (
                  <Combobox
                    required
                    menuPortalTarget={document.body}
                    size="small"
                    name="subscriber"
                    withForm={false}
                    placeholder="Subscriber"
                    onChange={(option) => subscriberFilter(option?.value)}
                    styles={{
                      menu: (provided) => ({
                        ...provided,
                        width: "160px",
                      }),
                    }}
                    value={
                      selectedSubscriberType
                        ? {
                            value: selectedSubscriberType,
                            label: subscriptionOptions.find(
                              (option) =>
                                option.value === selectedSubscriberType
                            )?.label,
                          }
                        : null
                    }
                    options={subscriptionOptions}
                  />
                )}

                <Combobox
                  required
                  menuPortalTarget={document.body}
                  size="small"
                  name="taskTemplate"
                  withForm={false}
                  placeholder={`${process.env.PUBLIC_OPS_TASK_TEMPLATE_FILTER_NAME} filter`}
                  onChange={(option) => taskFilter(option?.value)}
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      width: "160px",
                    }),
                  }}
                  value={
                    taskTemplateFilter
                      ? {
                          value: taskTemplateFilter,
                          label:
                            taskTemplateFilterMap[taskTemplateFilter] ||
                            "Not Completed",
                        }
                      : null
                  }
                  options={[
                    { value: NOT_CREATED, label: "Not Created" },
                    { value: NOT_STARTED_KEY, label: "Not Started" },
                    { value: NOT_COMPLETED, label: "Not Completed" },
                  ]}
                />

                {isAdmin && (
                  <Combobox
                    required
                    menuPortalTarget={document.body}
                    size="small"
                    name="sortDue"
                    withForm={false}
                    placeholder="Sort by"
                    onChange={(option) => duesFilter(option?.value)}
                    styles={{
                      menu: (provided) => ({
                        ...provided,
                        width: "160px",
                      }),
                    }}
                    value={
                      sortDueBy
                        ? {
                            value: sortDueBy,
                            label:
                              sortDueBy === "DSC"
                                ? "Highest dues first"
                                : "Lowest dues first",
                          }
                        : null
                    }
                    options={[
                      { value: "DSC", label: "Highest dues first" },
                      { value: "ASC", label: "Lowest dues first" },
                    ]}
                  />
                )}
              </div>
            )}
          </div>
          <PaginationHandler
            page_num={paginationData.currentPage}
            per_page={paginationData.itemsPerPage}
            total_count={paginationData.totalItemCount}
            total_pages={paginationData.totalPage}
            isLoading={isFetching && !generateReport}
            showLoadingText
          />
        </div>

        <Divider />
      </div>
      <NewGroupCreationModal
        showModal={showProfileForm}
        setShowModal={setShowProfileForm}
        setShowEntityModal={setShowEntityModal}
        setUserDataPayLoad={setUserDataPayLoad}
        userDataPayload={userDataPayload}
        setGroupName={setGroupName}
      />
      <AddCrmEntityTableModal
        setShowGroupModal={setShowProfileForm}
        showModal={showEntityModal}
        setModalShow={setShowEntityModal}
        userDataPayload={userDataPayload}
        groupName={groupName}
      />
      {bulkCartAction.isOpen && <BulkCartActionModal {...bulkCartAction} />}
    </>
  );
}
